import React from 'react'
import { useDebounce } from 'use-debounce'
import { Timing } from '@cbgms/base/utils/timing'
import { ResultObjectType } from '@cbgms/api/modules/enums'
import { useQueryResultTypes } from './useQueryResultTypes'
import { useQueryClient } from '@cbgms/base/http/use-query'
import { SEARCH } from 'app/search/state/types'

export const useSearch = (resultTypes: ResultObjectType[], excludeFromRegularSearch?: ResultObjectType[]) => {
  // Increase loading timespan to include time of
  // changing query string and returning debounced query value.
  const [loading, setLoading] = React.useState(false)
  const queryClient = useQueryClient()
  const [query, setQuery] = React.useState('')
  const [debouncedQuery] = useDebounce(query, Timing.fastKeyboardInput, { leading: false })
  const { data: searchResponse, isInitialLoading } = useQueryResultTypes(
    checkEmptyQuery,
    debouncedQuery,
    resultTypes,
    9999,
    excludeFromRegularSearch
  )

  React.useEffect(() => {
    setLoading(isInitialLoading || query !== debouncedQuery)
  }, [isInitialLoading, query, debouncedQuery])

  React.useEffect(() => {
    // effect to clear cache when query is cleared
    // clear cache to allow users to restart with fresh results whenever a selection is made, or the box is cleared
    // be careful; this has to run as side effect, calling `invalidateQueries` directly after setState will
    // cause the query to run with the old value.
    if (query === '') {
      queryClient.invalidateQueries([SEARCH])
    }
  }, [query, queryClient])

  function checkEmptyQuery(q: string) {
    return q !== '' && query !== ''
  }

  const clearQuery = () => {
    setQuery('')
  }

  return { loading, setLoading, query, setQuery, clearQuery, searchResponse }
}
